import React from "react";
import { useDispatch } from "react-redux";
import clsx from "clsx";
import PropTypes from "prop-types";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import {
  Tooltip,
  TooltipTrigger,
  tooltipVariants,
} from "@visiblethread/react-components/dist/components/Tooltip";
import TextCell from "@visiblethread/react-components/dist/components/Table/cells/TextCell";

import { displayWildcardPopup } from "slices/dictionary/dictionarySlice";
import { ReactComponent as Arrow } from "icons/dictionary/arrow-right-encircled.svg";
import { dictionaryTermTypes } from "constants/dictionary";
import { ReactComponent as Regex } from "icons/dictionary/Regex.svg";
import { ReactComponent as Wildcard } from "icons/dictionary/Wildcard.svg";

import { termProps } from "layout/dictionary/edit/types";

const TermName = ({
  term,
  term: { name, error = "", newlyCreated, dictionaryTermType },
  isSelected,
  disabled,
  isEditMode,
  onEditorClose,
  handleCellChange,
}) => {
  const dispatch = useDispatch();
  const expressionType =
    dictionaryTermType === dictionaryTermTypes.WILDCARD_EXPRESSION ||
    dictionaryTermType === dictionaryTermTypes.REGULAR_EXPRESSION;

  const handleNameChange = (newTextCell) => {
    // We should return an updated component from here. It is usual table flow for updating data
    handleCellChange(
      <TermName
        term={{ ...term, name: newTextCell.props.text }}
        isSelected={isSelected}
        isEditMode={isEditMode}
        disabled={disabled}
        onEditorClose={onEditorClose}
      />
    );
  };

  const handleEditClick = () => {
    // Display a popup where user will set term as wildcard or regexp
    dispatch(displayWildcardPopup({ displayed: true, term }));
  };

  const iconWithTooltip = (Icon, tooltipText) => {
    return (
      <TooltipTrigger content={tooltipText}>
        <Icon
          className={clsx("term_name__left__type_icon", {
            disabled: isEditMode,
          })}
        />
      </TooltipTrigger>
    );
  };

  const wildCardIcon = () => iconWithTooltip(Wildcard, "Wildcard");
  const regexIcon = () => iconWithTooltip(Regex, "Regex");

  return (
    <div className="term_name" data-testid="term-name">
      {expressionType ? (
        <span className="term_name__left">
          <span className={clsx("term_name__left__text", { disabled })}>
            {name}
          </span>
          {dictionaryTermType === dictionaryTermTypes.WILDCARD_EXPRESSION &&
            wildCardIcon()}
          {dictionaryTermType === dictionaryTermTypes.REGULAR_EXPRESSION &&
            regexIcon()}
        </span>
      ) : (
        <TextCell
          text={name}
          handleCellChange={handleNameChange}
          isSelected={isSelected}
          disabled={disabled}
          viewOnly={!isEditMode}
          error={error}
          editOnSelect={newlyCreated}
          onEditorClose={newlyCreated ? onEditorClose : undefined}
        />
      )}
      {isEditMode && expressionType && (
        <span className="term_name__right">
          {error && (
            <span
              className="term_name__right__error_icon"
              data-testid="error-icon"
            >
              <Tooltip content={error} variant={tooltipVariants.information} />
            </span>
          )}
          <Button
            content={
              <div className="term_name__right__edit_button">
                <span>Edit</span>
                <Arrow role="img" aria-label="arrow" />
              </div>
            }
            variant={buttonVariants.borderless}
            onClick={handleEditClick}
          />
        </span>
      )}
    </div>
  );
};

TermName.defaultProps = {
  isSelected: false,
  disabled: false,
  isEditMode: false,
};

TermName.propTypes = {
  /**
   * The actual term data.
   */
  term: PropTypes.shape(termProps).isRequired,
  /**
   * Equals true if this cell is selected.
   */
  isSelected: PropTypes.bool,
  /**
   * Equals true if this cell should be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Equals true if edit mode is on.
   */
  isEditMode: PropTypes.bool,
  /**
   * This fuction is used to set "newlyCreated" term field to "false" after first edit.
   */
  onEditorClose: PropTypes.func,
  /**
   * Function to update the cell.
   */
  handleCellChange: PropTypes.func,
};

export default TermName;
